import { create } from "zustand";

export type CoachLevel = 'beginner' | 'advanced';

export type EngineChoice =
  // Any Maia-trained Elo (600–2600, see utils/maiaElo) — was a narrow 1100–1900 literal set.
  | { kind: "maia"; elo: number }
  | { kind: "stockfish" };

interface SettingsState {
  engineDepth: number;
  multiPV: number;
  showEvalBar: boolean;
  showEngineArrows: boolean;
  coachLevel: CoachLevel;
  showStockfishAnalysis: boolean;
  showWinGraph: boolean;
  engineChoice: EngineChoice;
  setEngineDepth: (v: number) => void;
  setMultiPV: (v: number) => void;
  setShowEvalBar: (v: boolean) => void;
  setShowEngineArrows: (v: boolean) => void;
  setCoachLevel: (v: CoachLevel) => void;
  setShowStockfishAnalysis: (v: boolean) => void;
  setShowWinGraph: (v: boolean) => void;
  setEngineChoice: (v: EngineChoice) => void;
}

function loadSetting<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`chess-deck:${key}`);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch { return fallback; }
}

function saveSetting(key: string, value: unknown) {
  localStorage.setItem(`chess-deck:${key}`, JSON.stringify(value));
}

const DEFAULT_ENGINE_CHOICE: EngineChoice = { kind: "maia", elo: 1500 };

function loadEngineChoice(): EngineChoice {
  const saved = loadSetting<EngineChoice | null>("engineChoice", null);
  if (saved && saved.kind) return saved;
  // Migrate from per-context map format
  const map = loadSetting<{ analyze?: EngineChoice } | null>("engineChoice", null);
  if (map && map.analyze && map.analyze.kind) {
    const choice = map.analyze;
    saveSetting("engineChoice", choice);
    return choice;
  }
  // Migrate from legacy single-choice key
  const legacy = loadSetting<EngineChoice | null>("engineWidgetChoice", null);
  if (legacy && legacy.kind) {
    saveSetting("engineChoice", legacy);
    return legacy;
  }
  return DEFAULT_ENGINE_CHOICE;
}

// #302: `maiaElo`, the rating the old Maia Explorer slider and Play's old setup stored, is gone. Nothing has written it
// since the #263/#264 rebuilds, but the value they left kept centring the Moves tile's strip (2500 for a 1361 game).
// Cleared here, once per load, so a reader added later can't pick it up again.
try { localStorage.removeItem("chess-deck:maiaElo"); } catch { /* storage unavailable */ }

const useSettingsStore = create<SettingsState>((set) => ({
  engineDepth: loadSetting("engineDepth", 14),
  multiPV: loadSetting("multiPV", 3),
  showEvalBar: loadSetting("showEvalBar", true),
  showEngineArrows: loadSetting("showEngineArrows", true),
  coachLevel: loadSetting<CoachLevel>("coachLevel", "beginner"),
  showStockfishAnalysis: loadSetting("showStockfishAnalysis", false),
  showWinGraph: loadSetting("showWinGraph", true),
  engineChoice: loadEngineChoice(),

  setEngineDepth: (v) => { saveSetting("engineDepth", v); set({ engineDepth: v }); },
  setMultiPV: (v) => { saveSetting("multiPV", v); set({ multiPV: v }); },
  setShowEvalBar: (v) => { saveSetting("showEvalBar", v); set({ showEvalBar: v }); },
  setShowEngineArrows: (v) => { saveSetting("showEngineArrows", v); set({ showEngineArrows: v }); },
  setCoachLevel: (v) => { saveSetting("coachLevel", v); set({ coachLevel: v }); },
  setShowStockfishAnalysis: (v) => { saveSetting("showStockfishAnalysis", v); set({ showStockfishAnalysis: v }); },
  setShowWinGraph: (v) => { saveSetting("showWinGraph", v); set({ showWinGraph: v }); },
  setEngineChoice: (v) => { saveSetting("engineChoice", v); set({ engineChoice: v }); },
}));

export default useSettingsStore;
